import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@lib/cn";
import { IconX } from "@tabler/icons-react";
import type { ReactNode, Ref } from "react";
import { SlidesLibraryFloatingActions } from "./slides-library-floating-actions";
import { SlidesLibrarySearch } from "./slides-library-search";
import { SLIDES_CONTAINER_ID } from "../constants";
import styles from "../styles/dialog.module.scss";
import { useSlidesLibraryStoreShallow } from "../store";

export const SlideLibraryDialogShell = ({
  dialogId,
  title,
  headerExtra,
  sidebar,
  children,
  scrollKey,
  onClose,
  containerRef,
}: {
  dialogId: string;
  title: string;
  headerExtra?: ReactNode;
  sidebar: ReactNode;
  children: ReactNode;
  scrollKey?: string;
  onClose: () => void;
  containerRef?: Ref<HTMLDivElement>;
}) => {
  const hideSearch = useSlidesLibraryStoreShallow(
    (s) => s.templateTab === "import-from-stories" && !!s.importStoriesSelectedStoryId,
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        styles.shadow,
        "tw-flex tw-h-[700px] tw-w-[1024px] tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white",
      )}
    >
      {/* Header */}
      <div className="tw-shrink-0">
        <div className="tw-flex tw-h-16 tw-items-center tw-justify-between tw-pl-6 tw-pr-4">
          <span className="tw-font-sans tw-text-lg tw-font-semibold tw-leading-7 tw-text-blue">
            {title}
          </span>
          <button
            type="button"
            className="tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-transparent tw-outline-none hover:tw-bg-blue-50"
            onClick={onClose}
          >
            <IconX
              size={16}
              stroke={1.5}
              className="tw-fill-none tw-text-blue"
            />
          </button>
        </div>
        {headerExtra}
      </div>

      {/* Body */}
      <div className="tw-flex tw-flex-1 tw-overflow-hidden tw-border-t tw-border-solid tw-border-blue-100">
        {/* Sidebar */}
        <div className="tw-w-64 tw-shrink-0 tw-overflow-y-auto tw-border-r tw-border-solid tw-border-blue-100 tw-bg-blue-50">
          {sidebar}
        </div>

        {/* Main content */}
        <div
          className="tw-relative tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden"
          id={SLIDES_CONTAINER_ID}
        >
          {!hideSearch && (
            <div className="tw-shrink-0 tw-bg-white tw-px-6 tw-py-4">
              <SlidesLibrarySearch fullWidth />
            </div>
          )}
          <ScrollArea key={scrollKey} className="tw-flex-1">
            {children}
          </ScrollArea>
          <SlidesLibraryFloatingActions dialogId={dialogId} />
        </div>
      </div>
    </div>
  );
};
